import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './style.css';
import { 
  FiMoreVertical, 
  FiFileText,
  FiImage,
  FiClock,
  FiLink,
  FiUser,
  FiChevronDown,
  FiEdit3
} from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import React Quill styles
import { getBlog, updateBlog } from '../../store/Blog/blogSlice';
import { fetchCategories, createCategory } from '../../store/Blog/categoriesSlice';
import { fetchTags, createTag } from '../../store/Blog/tagsSlice';

const BlogView = () => {
  const { id } = useParams(); // Get blog ID from URL
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const blog = useSelector((state) => state.blogs.blogs.find(b => b._id === id));
  const { loading: blogLoading, error: blogError } = useSelector(state => state.blogs);
  const { categories: allCategories, loading: catLoading, error: catError } = useSelector(state => state.blogCategories);
  const { tags: allTags, loading: tagLoading, error: tagError } = useSelector(state => state.tags);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    status: 'Draft',
    categories: [],
    tags: [],
    featuredImage: null,
    galleryImages: [],
  });
  const [newTag, setNewTag] = useState('');
  const [featuredImagePreview, setFeaturedImagePreview] = useState('');
  const [galleryPreviews, setGalleryPreviews] = useState([]);

  const BaseUrl = process.env.REACT_APP_BASH_IMG_URL + '/';

  // Fetch blog data and categories/tags when component mounts
  useEffect(() => {
    if (id) {
      dispatch(getBlog(id));
    }
    dispatch(fetchCategories());
    dispatch(fetchTags());
  }, [id, dispatch]);

  // Update local state when blog data is fetched
  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title || '',
        content: blog.content || '',
        excerpt: blog.excerpt || '',
        status: blog.status || 'Draft',
        categories: blog.categories?.map(cat => cat._id) || [],
        tags: blog.tags?.map(tag => tag._id) || [],
        featuredImage: null, // File input starts empty; preview is set below
        galleryImages: [], // File input starts empty; preview is set below
      });
      // Set image previews if they exist
      if (blog.featuredImage) {
        setFeaturedImagePreview(`${BaseUrl}${blog.featuredImage}`);
      }
      if (blog.galleryImages?.length > 0) {
        setGalleryPreviews(blog.galleryImages.map(img => `${BaseUrl}${img}`));
      }
    }
  }, [blog, BaseUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      content: value,
    }));
  };

  const handleFeaturedImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Featured image must be under 10MB');
        return;
      }
      setFormData(prev => ({ ...prev, featuredImage: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setFeaturedImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const validFiles = files.filter(file => {
        if (file.size > 10 * 1024 * 1024) {
          alert(`Image ${file.name} exceeds 10MB and will be skipped`);
          return false;
        }
        return true;
      });
      setFormData(prev => ({ ...prev, galleryImages: validFiles }));
      const previews = [];
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result);
          if (previews.length === validFiles.length) {
            setGalleryPreviews(previews);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => {
      const newCategories = prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId];
      return { ...prev, categories: newCategories };
    });
  };

  const handleTagToggle = (tagId) => {
    setFormData(prev => {
      const newTags = prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId];
      return { ...prev, tags: newTags };
    });
  };

  const handleAddNewCategory = async () => {
    const newCategory = prompt('Enter new category name:');
    if (newCategory?.trim()) {
      try {
        const result = await dispatch(createCategory({ name: newCategory.trim() })).unwrap();
        setFormData(prev => ({
          ...prev,
          categories: [...prev.categories, result._id],
        }));
      } catch (err) {
        alert(`Error creating category: ${err.message || err}`);
      }
    }
  };

  const handleAddNewTag = async () => {
    if (newTag.trim()) {
      try {
        const result = await dispatch(createTag({ name: newTag.trim() })).unwrap();
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, result._id],
        }));
        setNewTag('');
      } catch (err) {
        alert(`Error creating tag: ${err.message || err}`);
      }
    }
  };

  const handleSubmit = async (isPublish = false) => {
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('content', formData.content);
    if (formData.excerpt) formDataToSend.append('excerpt', formData.excerpt);
    formDataToSend.append('status', isPublish ? 'Published' : 'Draft');
    if (isPublish) formDataToSend.append('publishDate', new Date().toISOString());
    formData.categories.forEach(cat => formDataToSend.append('categories', cat));
    formData.tags.forEach(tag => formDataToSend.append('tags', tag));
    if (formData.featuredImage) formDataToSend.append('featuredImage', formData.featuredImage);
    formData.galleryImages.forEach(img => formDataToSend.append('galleryImages', img));

    // Log FormData and calculate size
    let totalSize = 0;
    console.log('Submitting FormData:');
    for (const [key, value] of formDataToSend.entries()) {
      const size = value instanceof File ? value.size : new TextEncoder().encode(value).length;
      totalSize += size;
      console.log(`FormData: ${key} = ${value instanceof File ? `${value.name} (${(size / 1024).toFixed(2)} KB)` : value}`);
    }
    console.log(`Total FormData size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);

    try {
      await dispatch(updateBlog({ id, blogData: formDataToSend })).unwrap();
      console.log('Post updated successfully');
      navigate('/blogs');
    } catch (err) {
      console.error('Submission error:', err);
      alert(`Error: ${err.message || 'Failed to update post'}`);
    }
  };

  const handleSaveDraft = () => handleSubmit(false);
  const handlePublish = () => handleSubmit(true);

  // React Quill toolbar options
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  if (blogLoading || catLoading || tagLoading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (blogError || catError || tagError || !blog) {
    return (
      <div className="alert alert-danger text-center my-5">
        {blogError || catError || tagError || 'Blog not found'}
      </div>
    );
  }

  return (
    <div className="create-post product-wrap col-md-10 mx-auto mt-4 p-4" style={{ background: '#F6F6F6', borderRadius: '8px' }}>
      <div className="container">
        <div className="row">
          <div className="new-post-header d-flex justify-content-between align-items-center mb-4">
            <h2 className="m-0" style={{ fontSize: '1.75rem', fontWeight: '600' }}>Update Post</h2>
            <div className="nph-2">
              <button
                className="save-draft-btn btn btn-outline-secondary me-2"
                onClick={handleSaveDraft}
                disabled={blogLoading || !formData.title.trim()}
                style={{ padding: '6px 12px' }}
              >
                {blogLoading ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                className="btn-cls btn btn-primary"
                onClick={handlePublish}
                disabled={blogLoading || !formData.title.trim()}
                style={{ padding: '6px 12px' }}
              >
                {blogLoading ? 'Publishing...' : 'Publish'}
              </button>
              <FiMoreVertical size={20} className="ms-2" style={{ cursor: 'pointer' }} />
            </div>
          </div>

          {(blogError || catError || tagError) && (
            <div className="alert alert-danger">{blogError || catError || tagError}</div>
          )}

          <div className="col-md-9">
            <input
              className="add-title-text form-control mb-3"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Add Title"
              required
            />
            <ReactQuill
              value={formData.content}
              onChange={handleContentChange}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Edit your post content..."
              className="mb-4"
            />

            <div className="lte-sec mt-3">
              <h5>Featured Image</h5>
              <label className="btn btn-outline-secondary mb-3">
                <FiImage size={20} className="me-2" />
                {formData.featuredImage || featuredImagePreview ? 'Change Image' : 'Add Featured Image'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFeaturedImageChange}
                  style={{ display: 'none' }}
                />
              </label>
              {featuredImagePreview && (
                <div className="mb-3">
                  <img
                    src={featuredImagePreview}
                    alt="Featured preview"
                    className="img-thumbnail rounded"
                    style={{ maxHeight: '200px', width: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.src = '/placeholder-image.jpg'; // Fallback image
                      e.target.alt = 'Failed to load featured image';
                    }}
                  />
                </div>
              )}

              <h5 className="mt-4">Gallery</h5>
              <label className="btn btn-outline-secondary mb-3">
                <FiImage size={20} className="me-2" />
                {formData.galleryImages.length > 0 || galleryPreviews.length > 0 ? 'Add More Images' : 'Add Gallery Images'}
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleGalleryImagesChange}
                  style={{ display: 'none' }}
                />
              </label>
              <p className="text-muted">Upload images for slider gallery post type (max 10MB each)</p>

              {galleryPreviews.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {galleryPreviews.map((preview, index) => (
                    <img
                      key={index}
                      src={preview}
                      alt={`Gallery preview ${index}`}
                      className="img-thumbnail rounded"
                      style={{ height: '100px', width: '100px', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.src = '/placeholder-image.jpg'; // Fallback image
                        e.target.alt = 'Failed to load gallery image';
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="col-md-3">
            <div className="sidebar">
              <div className="side-post-title mb-3 p-2 bg-light rounded">
                <span>
                  <FiFileText size={18} className="me-2" />
                  {formData.title || 'No Title'}
                </span>
              </div>

              <div className="excert mt-3 mb-3">
                <div className="d-flex align-items-center mb-2">
                  <FiEdit3 size={16} className="me-2" />
                  <label>Excerpt</label>
                </div>
                <textarea
                  className="form-control"
                  name="excerpt"
                  placeholder="Add an excerpt…"
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  rows="3"
                  style={{ resize: 'none' }}
                />
                <small className="text-muted">A short description of your post</small>
              </div>

              <ul className="list-unstyled tab-list mt-3 mb-3 p-2 bg-light rounded">
                <li className="mb-2">
                  <FiClock size={16} className="me-2" />
                  <span>Status:</span> <span className="fw-bold">{formData.status}</span>
                </li>
                <li className="mb-2">
                  <FiClock size={16} className="me-2" />
                  <span>Last Edited:</span> <span className="fw-bold">{blog.updatedAt ? new Date(blog.updatedAt).toLocaleString() : 'a second ago'}</span>
                </li>
                <li className="mb-2">
                  <FiLink size={16} className="me-2" />
                  <span>Link:</span> <span className="fw-bold">{blog._id ? `/blogs/${blog._id}` : 'N/A'}</span>
                </li>
                <li>
                  <FiUser size={16} className="me-2" />
                  <span>Author:</span> <span className="fw-bold">{blog.author?.name || 'Admin'}</span>
                </li>
              </ul>

              <div className="side-action">
                <div className="w-100 s-wrap mb-3">
                  <a
                    href="#categoriesCollapse"
                    className="d-flex justify-content-between align-items-center p-2 bg-light rounded text-decoration-none"
                    data-bs-toggle="collapse"
                  >
                    <span>
                      <FiFileText size={16} className="me-2" />
                      Categories
                    </span>
                    <FiChevronDown size={18} />
                  </a>
                  <div className="collapse mt-2" id="categoriesCollapse">
                    <div className="card card-body p-2">
                      {allCategories.length > 0 ? (
                        allCategories.map(category => (
                          <div key={category._id} className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`cat-${category._id}`}
                              checked={formData.categories.includes(category._id)}
                              onChange={() => handleCategoryToggle(category._id)}
                            />
                            <label className="form-check-label" htmlFor={`cat-${category._id}`}>
                              {category.name}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted">No categories found</p>
                      )}
                      <button
                        className="btn btn-sm btn-link text-success p-0 mt-2 text-decoration-none"
                        onClick={handleAddNewCategory}
                      >
                        + Add New Category
                      </button>
                    </div>
                  </div>
                </div>

                <div className="w-100 s-wrap">
                  <a
                    href="#tagsCollapse"
                    className="d-flex justify-content-between align-items-center p-2 bg-light rounded text-decoration-none"
                    data-bs-toggle="collapse"
                  >
                    <span>
                      <FiFileText size={16} className="me-2" />
                      Tags
                    </span>
                    <FiChevronDown size={18} />
                  </a>
                  <div className="collapse mt-2" id="tagsCollapse">
                    <div className="card card-body p-2">
                      <div className="mb-3">
                        <label className="form-label">Add New Tag</label>
                        <div className="input-group">
                          <input
                            className="form-control form-control-sm"
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddNewTag();
                              }
                            }}
                            placeholder="Tag name"
                          />
                          <button
                            className="btn btn-sm btn-success"
                            onClick={handleAddNewTag}
                            disabled={!newTag.trim()}
                          >
                            Add
                          </button>
                        </div>
                        <small className="text-muted">Press Enter or click Add to create tag</small>
                      </div>

                      {allTags.length > 0 ? (
                        <div className="tag-container">
                          {allTags.map(tag => (
                            <div key={tag._id} className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`tag-${tag._id}`}
                                checked={formData.tags.includes(tag._id)}
                                onChange={() => handleTagToggle(tag._id)}
                              />
                              <label className="form-check-label" htmlFor={`tag-${tag._id}`}>
                                {tag.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted">No tags found</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogView;